import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { ChevronRight, ChevronDown, Home, FileText, Cpu, ClipboardPen, Settings, Shield, Code, ArrowDownNarrowWide, ArrowUpNarrowWide, Navigation, Search, X, PanelLeftClose, PanelLeft, FileChartColumnIncreasing, GanttChart, CircuitBoard, Wrench, Blocks } from 'lucide-react';
import { navigationConfig } from '../../config/navigationConfig';
import { useAuth } from '../../context/AuthContext';
import { useDatabaseSafe } from '../../context/DatabaseContext';

// Icon mapping for Level 1 sections
const sectionIcons = {
  'home': Home,
  'engineering': CircuitBoard,
  'documentation': FileText,
  'project-management': GanttChart,
  'equipment': Cpu,
  'data-entry': ClipboardPen,
  'configuration': Settings,
  'reports': FileChartColumnIncreasing,
  'integration-dashboard': Blocks,
  'utilities': Wrench,
  'administration': Shield,
  'development': Code
};

// Flatten permission-filtered nav into searchable results with breadcrumb
const flattenNav = (items, breadcrumb = []) => {
  const results = [];
  items.forEach(item => {
    const crumb = [...breadcrumb, item.label];
    if (!item.headerOnly && item.destination) {
      results.push({ ...item, breadcrumb });
    }
    if (item.children) {
      results.push(...flattenNav(item.children, crumb));
    }
  });
  return results;
};

// Bold the matching substring in a label
const highlightMatch = (label, query) => {
  const idx = label.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return label;
  return <>{label.slice(0, idx)}<strong>{label.slice(idx, idx + query.length)}</strong>{label.slice(idx + query.length)}</>;
};

// Helper to find the path to a destination in the nav tree
const findPathToDestination = (items, destination, path = []) => {
  for (const item of items) {
    const currentPath = [...path, item.id];
    if (item.destination === destination) {
      return currentPath;
    }
    if (item.children) {
      const found = findPathToDestination(item.children, destination, currentPath);
      if (found) return found;
    }
  }
  return null;
};

const NavItem = ({ item, level = 1, currentPage, setCurrentPage, expandedItems, toggleExpand, isCollapsed, itemRefs }) => {
  const hasChildren = item.children && item.children.length > 0;
  const isExpanded = expandedItems.includes(item.id);
  const isActive = currentPage === item.destination;
  const Icon = level === 1 ? sectionIcons[item.id] : null;
  
  const handleClick = () => {
    if (!item.headerOnly && item.destination) {
      setCurrentPage(item.destination);
      // Expand if has children but never collapse — chevron handles collapse
      if (hasChildren && !isCollapsed && !isExpanded) {
        toggleExpand(item.id);
      }
    } else if (hasChildren && !isCollapsed) {
      toggleExpand(item.id);
    }
  };

  const handleChevronClick = (e) => {
    e.stopPropagation();
    if (hasChildren && !isCollapsed) toggleExpand(item.id);
  };

  return (
    <div className="stui-nav-item-container">
      <button
        ref={el => { if (itemRefs) itemRefs.current[item.destination] = el; }}
        onClick={handleClick}
        className={`stui-nav-item stui-nav-item-level-${level} ${isActive ? 'active' : ''}`}
        data-tooltip={item.label}
        data-nav-id={item.id}
      >
        <div className="stui-nav-item-content">
          {hasChildren && !isCollapsed && (
            <span className="stui-nav-item-chevron" onClick={handleChevronClick}>
              {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </span>
          )}
          {!hasChildren && level > 1 && !isCollapsed && <span className="stui-nav-item-spacer" />}
          {Icon && <Icon size={16} className="stui-nav-item-icon" />}
          {!isCollapsed && (
            <>
              <span className="stui-nav-item-label">
                {item.label}
              </span>
              {item.future && <span className="stui-nav-future-badge">FUTURE</span>}
            </>
          )}
        </div>
      </button>
      
      {hasChildren && isExpanded && !isCollapsed && (
        <div className="stui-nav-children">
          {item.children.map(child => (
            <NavItem
              key={child.id}
              item={child}
              level={level + 1}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              expandedItems={expandedItems}
              toggleExpand={toggleExpand}
              isCollapsed={isCollapsed}
              itemRefs={itemRefs}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Data entry module permission key map: nav item id → AppSec permission key
const modulePermMap = {
  'estimating': 'module.estimating',
  'integration': 'module.integration',
  'submittals': 'module.submittal',
  'sub-contractors': 'module.subcontractor',
  'purchasing': 'module.purchasing',
  'qaqc': 'module.qaqc',
  'material-transfer': 'module.transmittal',
  'commissioning': 'module.commissioning'
};

const Sidebar = ({ currentPage, setCurrentPage, sidebarTarget, clearSidebarTarget }) => {
  const { isSysAdmin, isAdmin, hasPermission } = useAuth();
  const dbContext = useDatabaseSafe();
  const dbSlug = dbContext?.dbSlug || '';
  const [expandedItems, setExpandedItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem('pmd-nav-expanded') || '[]'); } catch { return []; }
  });
  const [isCollapsed, setIsCollapsed] = useState(() => {
    return localStorage.getItem('pmd-nav-collapsed') === 'true';
  });
  const [searchMode, setSearchMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isCurrentVisible, setIsCurrentVisible] = useState(false);
  const searchInputRef = useRef(null);
  const itemRefs = useRef({});
  const navRef = useRef(null);

  // Persist nav scroll position across route changes. The sidebar unmounts when
  // React Router swaps AdminLayout <-> AppContent (crossing between /:dbSlug/* and
  // /admin|/dev-tool routes), which resets scrollTop. Restore on mount and save on
  // scroll so users who scrolled deep into the tree don't lose their place.
  useLayoutEffect(() => {
    const stored = sessionStorage.getItem('pmd-nav-scrolltop');
    if (stored && navRef.current) {
      const v = parseInt(stored, 10);
      if (!Number.isNaN(v)) navRef.current.scrollTop = v;
    }
    const nav = navRef.current;
    if (!nav) return undefined;
    let raf = null;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        sessionStorage.setItem('pmd-nav-scrolltop', String(nav.scrollTop));
        raf = null;
      });
    };
    nav.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      nav.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  // Track whether the active nav item is visible in the scroll area
  useEffect(() => {
    const el = itemRefs.current[currentPage];
    if (!el || !navRef.current) { setIsCurrentVisible(false); return; }
    const observer = new IntersectionObserver(
      ([entry]) => setIsCurrentVisible(entry.isIntersecting),
      { root: navRef.current, threshold: 1.0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [currentPage, expandedItems]);

  // Filter navigation items based on AppSec permissions
  const filterNavItems = useMemo(() => {
    const perm = (key) => hasPermission(key, dbSlug);

    const filterItem = (item) => {
      // SysAdmin sees everything
      if (isSysAdmin) {
        if (item.children) return { ...item, children: item.children.map(filterItem).filter(Boolean) };
        return item;
      }

      // Engineering: visible to everyone
      if (item.id === 'engineering') {
        if (item.children) return { ...item, children: item.children.map(filterItem).filter(Boolean) };
        return item;
      }

      // Data Entry: filter by individual module.* permissions
      if (item.id === 'data-entry') {
        const hasAny = Object.values(modulePermMap).some(k => perm(k));
        if (!hasAny) return null;
        if (item.children) {
          const filtered = item.children.map(child => {
            const permKey = modulePermMap[child.id];
            if (permKey && !perm(permKey)) return null;
            return filterItem(child);
          }).filter(Boolean);
          if (filtered.length === 0) return null;
          return { ...item, children: filtered };
        }
        return item;
      }

      // Reports: module.reports (User+ by DefRoles)
      if (item.id === 'reports') {
        if (!perm('module.reports')) return null;
        if (item.children) return { ...item, children: item.children.map(filterItem).filter(Boolean) };
        return item;
      }

      // Project Management: module.projectmanagement (AppAdmin only by DefRoles)
      if (item.id === 'project-management') {
        if (!perm('module.projectmanagement')) return null;
        if (item.children) return { ...item, children: item.children.map(filterItem).filter(Boolean) };
        return item;
      }

      // Utilities: feature.utilities.* (PowerUser+ by DefRoles)
      if (item.id === 'utilities') {
        if (!perm('feature.utilities.bulkimport') && !perm('feature.utilities.powerquery')) return null;
        if (item.children) {
          const filtered = item.children.map(child => {
            if (child.id === 'import-template' && !perm('feature.utilities.bulkimport')) return null;
            if (child.id === 'power-query' && !perm('feature.utilities.powerquery')) return null;
            return filterItem(child);
          }).filter(Boolean);
          if (filtered.length === 0) return null;
          return { ...item, children: filtered };
        }
        return item;
      }

      // Administration: SysAdmin, or users with admin.admin globally granted
      if (item.id === 'administration') {
        if (!isSysAdmin && !hasPermission('admin.admin', 'global')) return null;
        if (item.children) {
          const isProd = !!window.__config__?.IS_PROD;
          const filtered = item.children.map(child => {
            if (isProd && child.id === 'admin-database') return null;
            return filterItem(child);
          }).filter(Boolean);
          return { ...item, children: filtered };
        }
        return item;
      }

      // Integration: gated by integration.integration permission (per-DB)
      if (item.id === 'integration-dashboard') {
        if (!perm('integration.integration')) return null;
        if (item.children) return { ...item, children: item.children.map(filterItem).filter(Boolean) };
        return item;
      }

      // Development: gated by development.development permission (global — not DB-scoped)
      if (item.id === 'development') {
        if (!isSysAdmin && !hasPermission('development.development', 'global')) return null;
        if (item.children) return { ...item, children: item.children.map(filterItem).filter(Boolean) };
        return item;
      }

      // Default: visible to all (home, any future top-level items)
      if (item.children) return { ...item, children: item.children.map(filterItem).filter(Boolean) };
      return item;
    };

    return navigationConfig.map(filterItem).filter(Boolean);
  }, [isSysAdmin, isAdmin, dbSlug, hasPermission]); // eslint-disable-line react-hooks/exhaustive-deps

  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const q = searchQuery.trim();
    const seen = new Set();
    return flattenNav(filterNavItems)
      .filter(item => item.label.toLowerCase().includes(q.toLowerCase()))
      .filter(item => {
        if (seen.has(item.destination)) return false;
        seen.add(item.destination);
        return true;
      });
  }, [filterNavItems, searchQuery]);

  const openSearch = () => {
    setSearchMode(true);
    setTimeout(() => searchInputRef.current?.focus(), 50);
  };

  const closeSearch = () => {
    setSearchMode(false);
    setSearchQuery('');
  };

  const handleSearchSelect = (destination) => {
    setCurrentPage(destination);
    closeSearch();
    const path = findPathToDestination(navigationConfig, destination);
    if (path) {
      const parents = path.slice(0, -1);
      setExpandedItems(prev => {
        const next = [...prev];
        parents.forEach(id => { if (!next.includes(id)) next.push(id); });
        localStorage.setItem('pmd-nav-expanded', JSON.stringify(next));
        return next;
      });
    }
    setTimeout(() => {
      const el = itemRefs.current[destination];
      if (el && navRef.current) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  // Auto-expand ancestor nav nodes whenever currentPage changes so the active
  // item is always visible. Only expands parents — never the item itself —
  // so the user retains manual chevron control over group expansion.
  useEffect(() => {
    if (!currentPage) return;
    const path = findPathToDestination(navigationConfig, currentPage);
    if (!path || path.length <= 1) return;
    const parents = path.slice(0, -1);
    setExpandedItems(prev => {
      const next = [...prev];
      let changed = false;
      parents.forEach(id => { if (!next.includes(id)) { next.push(id); changed = true; } });
      if (!changed) return prev;
      localStorage.setItem('pmd-nav-expanded', JSON.stringify(next));
      return next;
    });
  }, [currentPage]);

  // Auto-expand and scroll when sidebarTarget changes (FK navigation)
  useEffect(() => {
    if (sidebarTarget) {
      // Find path to target in nav tree
      const path = findPathToDestination(navigationConfig, sidebarTarget);
      
      if (path) {
        // Expand all parent items (exclude the last one which is the target itself)
        const parentsToExpand = path.slice(0, -1);
        setExpandedItems(prev => {
          const newExpanded = [...prev];
          parentsToExpand.forEach(id => {
            if (!newExpanded.includes(id)) {
              newExpanded.push(id);
            }
          });
          return newExpanded;
        });

        // Scroll to the item after a brief delay to let expansion render
        setTimeout(() => {
          const targetEl = itemRefs.current[sidebarTarget];
          if (targetEl && navRef.current) {
            targetEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      }

      // Clear the target after handling
      if (clearSidebarTarget) {
        clearSidebarTarget();
      }
    }
  }, [sidebarTarget, clearSidebarTarget]);

  const toggleExpand = (itemId) => {
    setExpandedItems(prev => {
      const next = prev.includes(itemId) ? prev.filter(id => id !== itemId) : [...prev, itemId];
      localStorage.setItem('pmd-nav-expanded', JSON.stringify(next));
      return next;
    });
  };

  const expandAll = () => {
    const allExpandableIds = [];
    const collectIds = (items) => {
      items.forEach(item => {
        if (item.children && item.children.length > 0) {
          allExpandableIds.push(item.id);
          collectIds(item.children);
        }
      });
    };
    collectIds(navigationConfig);
    setExpandedItems(allExpandableIds);
    localStorage.setItem('pmd-nav-expanded', JSON.stringify(allExpandableIds));
  };

  const collapseAll = () => {
    setExpandedItems([]);
    localStorage.setItem('pmd-nav-expanded', JSON.stringify([]));
  };

  const allExpandableIds = useMemo(() => {
    const ids = [];
    const collect = (items) => items.forEach(item => {
      if (item.children?.length > 0) { ids.push(item.id); collect(item.children); }
    });
    collect(navigationConfig);
    return ids;
  }, []);

  const isAllExpanded = allExpandableIds.every(id => expandedItems.includes(id));
  const isAllCollapsed = expandedItems.length === 0;

  const jumpToSelected = () => {
    const path = findPathToDestination(navigationConfig, currentPage);
    if (path) {
      const parents = path.slice(0, -1);
      setExpandedItems(prev => {
        const next = [...prev];
        parents.forEach(id => { if (!next.includes(id)) next.push(id); });
        localStorage.setItem('pmd-nav-expanded', JSON.stringify(next));
        return next;
      });
    }
    setTimeout(() => {
      const el = itemRefs.current[currentPage];
      if (el && navRef.current) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  // Separate Home from other nav items (using permission-filtered nav)
  const homeItem = filterNavItems.find(item => item.id === 'home');
  const otherNavItems = filterNavItems.filter(item => item.id !== 'home');

  return (
    <aside className={`stui-nav-sidebar ${isCollapsed ? 'stui-collapsed' : ''}`}>
      {/* Home row with collapse toggle */}
      {homeItem && (
        <div className="stui-nav-home-row">
          <button
            ref={el => { itemRefs.current[homeItem.destination] = el; }}
            onClick={() => setCurrentPage(homeItem.destination)}
            className={`stui-nav-item stui-nav-item-level-1 stui-nav-item-home ${currentPage === homeItem.destination ? 'active' : ''}`}
            data-tooltip={homeItem.label}
          >
            <div className="stui-nav-item-content">
              <Home size={16} className="stui-nav-item-icon" />
              {!isCollapsed && <span className="stui-nav-item-label">{homeItem.label}</span>}
            </div>
          </button>
          <button 
            className="stui-nav-collapse-toggle"
            onClick={() => { const next = !isCollapsed; setIsCollapsed(next); localStorage.setItem('pmd-nav-collapsed', String(next)); }}
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isCollapsed ? <PanelLeft size={16} /> : <PanelLeftClose size={16} />}
          </button>
        </div>
      )}
      
      {/* Scrollable nav area — tree or search results */}
      <nav className="stui-nav-scroll" ref={navRef}>
        {searchMode && searchQuery.trim() ? (
          searchResults.length === 0 ? (
            <div className="stui-nav-search-empty">No results</div>
          ) : (
            searchResults.map(item => (
              <button
                key={item.id}
                className="stui-nav-search-item"
                onClick={() => handleSearchSelect(item.destination)}
              >
                <span className="stui-nav-search-label">{highlightMatch(item.label, searchQuery)}</span>
                {item.breadcrumb.length > 0 && (
                  <span className="stui-nav-search-crumb">{item.breadcrumb.join(' / ')}</span>
                )}
              </button>
            ))
          )
        ) : (
          otherNavItems.map(item => (
            <NavItem
              key={item.id}
              item={item}
              level={1}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              expandedItems={expandedItems}
              toggleExpand={toggleExpand}
              isCollapsed={isCollapsed}
              itemRefs={itemRefs}
            />
          ))
        )}
      </nav>

      {!isCollapsed && (
        <div className="stui-nav-footer">
          {searchMode ? (
            <>
              <input
                ref={searchInputRef}
                className="stui-nav-search-input"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onKeyDown={e => { if (e.key === 'Escape') closeSearch(); }}
                placeholder="Search navigation..."
              />
              <button onClick={closeSearch} className="stui-nav-footer-btn stui-nav-search-close" title="Close search">
                <X size={15} />
              </button>
            </>
          ) : (
            <>
              <button onClick={openSearch} className="stui-nav-footer-btn" title="Search navigation">
                <Search size={15} />
              </button>
              <button onClick={jumpToSelected} className={`stui-nav-footer-btn${isCurrentVisible ? ' disabled' : ''}`} title="Show current location" disabled={isCurrentVisible}>
                <Navigation size={15} />
              </button>
              <button onClick={expandAll} className={`stui-nav-footer-btn${isAllExpanded ? ' disabled' : ''}`} title="Expand all" disabled={isAllExpanded}>
                <ArrowDownNarrowWide size={15} />
              </button>
              <button onClick={collapseAll} className={`stui-nav-footer-btn${isAllCollapsed ? ' disabled' : ''}`} title="Collapse all" disabled={isAllCollapsed}>
                <ArrowUpNarrowWide size={15} />
              </button>
            </>
          )}
        </div>
      )}
    </aside>
  );
};

export default Sidebar;
